import { FC, useEffect, useMemo, useState } from 'react'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import cn from './index.module.css'
import Modal from '@common/Modal'
import classNames from 'classnames'
import { Modal as AntModal } from 'antd'
import { IcoRemove, InfoIcon } from '@/utils/icons'
import SelectField from '@pages/Admin/CourseSettings/components/SelectField/SelectField'
import TextField from '@pages/Admin/CourseSettings/components/TextField/TextField'
import CheckboxLabel from '@pages/Admin/CourseSettings/components/CheckboxLabel/CheckboxLabel'
import PrimaryButton from '@pages/Admin/CourseSettings/components/PrimaryButton/PrimaryButton'
import SecondaryButton from '@pages/Admin/CourseSettings/components/SecondaryButton/SecondaryButton'
import { ITeacherSalary, TeacherSalaryType } from '../../types'
import { useAddAlert } from '@/hooks/useAddAlert'
import UserCard from '@pages/Admin/Manage/tabs/ClassesPage/components/ShowUsersModal/components/UserCard/UserCard'
import {
  updateTeacherSalary,
  updateTeacherSalaryForAllGroups,
} from './utils/api'
import FieldWrapper from '@pages/Admin/CourseSettings/components/FieldWrapper/FieldWrapper'
import { useGetGroupName } from '@pages/Admin/PlatformSettings2/AddSettingsEmployeesPersonAccount/hooks/useGetGroupName'
import {
  getTeacherSalaryTooltip,
  TEACHER_SALARY_OPTIONS,
} from '../../utils/teacherSalary'
import jclasses from '@pages/Admin/CRM/styles/jsmodal.module.css'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'

interface EditTeacherSalaryModalProps {
  teacher?: IUser
  group?: IGroup
  teacherSalary?: ITeacherSalary | null
  duration?: number | null
  mode?: 'group' | 'all-groups'
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

const DEFAULT_SALARY_TYPE: TeacherSalaryType = 'per-lesson'
const MIN_AMOUNT_TOOLTIP =
  'Включив данный чек-бокс, вы сможете внести минимальную сумму, которую получит педагог вне зависимости от количества учащихся, пришедших на урок'

const normalizeNonNegativeNumberInput = (value: string) => {
  if (!value) return ''
  return value.replace('-', '')
}

const EditTeacherSalaryModalContent: FC<EditTeacherSalaryModalProps> = ({
  teacher,
  group,
  teacherSalary,
  duration,
  mode = 'group',
  open,
  onClose,
  onSuccess,
}) => {
  const { groupNameSingular } = useGetGroupName()
  const addAlert = useAddAlert()
  const isAllGroupsMode = mode === 'all-groups'
  const [submitting, setSubmitting] = useState(false)
  const [salaryType, setSalaryType] =
    useState<TeacherSalaryType>(DEFAULT_SALARY_TYPE)
  const [salaryValue, setSalaryValue] = useState('')
  const [durationMinutes, setDurationMinutes] = useState('')
  const [minAmountEnabled, setMinAmountEnabled] = useState(false)
  const [minAmount, setMinAmount] = useState('')

  useEffect(() => {
    if (!open) return

    setSalaryType(teacherSalary?.type ?? DEFAULT_SALARY_TYPE)
    setSalaryValue(
      teacherSalary?.salary !== null && teacherSalary?.salary !== undefined
        ? String(teacherSalary.salary)
        : '',
    )
    setDurationMinutes(
      duration !== null && duration !== undefined
        ? String(duration)
        : teacherSalary?.duration_minutes !== null &&
            teacherSalary?.duration_minutes !== undefined
          ? String(teacherSalary.duration_minutes)
          : '',
    )
    setMinAmountEnabled(!!teacherSalary?.min_amount_enabled)
    setMinAmount(
      teacherSalary?.min_amount !== null && teacherSalary?.min_amount !== undefined
        ? String(teacherSalary.min_amount)
        : '',
    )
  }, [duration, teacherSalary, open])

  const salaryTypeOptions = useMemo(() => TEACHER_SALARY_OPTIONS, [])

  const salaryLabel =
    salaryType === 'per-student-hour'
      ? 'Впишите зп за одного человека'
      : 'Впишите зарплату за одно занятие'
  const salaryTypeTooltip = getTeacherSalaryTooltip(salaryType)

  const showDurationField = salaryType === 'per-duration'
  const showMinAmountFields = salaryType === 'per-student-hour'

  const onSalaryChange = (value: string) => {
    setSalaryValue(normalizeNonNegativeNumberInput(value))
  }

  const onDurationChange = (value: string) => {
    setDurationMinutes(normalizeNonNegativeNumberInput(value))
  }

  const onMinAmountChange = (value: string) => {
    setMinAmount(normalizeNonNegativeNumberInput(value))
  }

  const onSubmit = async () => {
    if (!teacher?.id || (!isAllGroupsMode && !group?.id)) {
      addAlert({
        title: 'Ошибка',
        text: isAllGroupsMode
          ? 'Не удалось определить педагога'
          : 'Не удалось определить группу или педагога',
        type: 'error',
      })
      return
    }

    if (!salaryValue) {
      addAlert({
        title: 'Ошибка',
        text: 'Укажите зарплату',
        type: 'error',
      })
      return
    }

    if (showDurationField && !durationMinutes) {
      addAlert({
        title: 'Ошибка',
        text: 'Укажите длительность занятия',
        type: 'error',
      })
      return
    }

    if (showMinAmountFields && minAmountEnabled && minAmount === '') {
      addAlert({
        title: 'Ошибка',
        text: 'Укажите минимальную сумму',
        type: 'error',
      })
      return
    }

    setSubmitting(true)

    try {
      const payload = {
        teacher_id: Number(teacher.id),
        type: salaryType,
        salary: Number(salaryValue),
        duration_minutes:
          salaryType === 'per-student-hour'
            ? 60
            : showDurationField
              ? Number(durationMinutes)
              : null,
        min_amount_enabled: showMinAmountFields ? minAmountEnabled : false,
        min_amount:
          showMinAmountFields && minAmountEnabled ? Number(minAmount) : null,
      }

      const response = isAllGroupsMode
        ? await updateTeacherSalaryForAllGroups(payload)
        : await updateTeacherSalary({
            group_id: Number(group?.id),
            ...payload,
          })

      if (!response?.success) {
        throw new Error()
      }

      onSuccess?.()
      addAlert({
        title: 'Успешно',
        text: isAllGroupsMode
          ? 'Зарплата педагога обновлена для всех групп'
          : 'Зарплата педагога сохранена',
        type: 'success',
      })
      onClose()
    } catch (error) {
      addAlert({
        title: 'Ошибка',
        text: isAllGroupsMode
          ? 'Не удалось сохранить зарплату для всех групп педагога'
          : 'Не удалось сохранить зарплату педагога',
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const onConfirmSubmit = () => {
    if (!isAllGroupsMode) {
      onSubmit()
      return
    }

    AntModal.confirm({
      title: 'Обновить зарплату для всех групп',
      content:
        'Вы уверены, что хотите обновить зарплату педагога сразу для всех его групп?',
      icon: null,
      centered: true,
      maskClosable: true,
      className: jclasses.modal,
      okText: 'Обновить',
      cancelText: 'Отменить',
      onOk: () => onSubmit(),
    })
  }

  return (
    <div className={classNames(mclasses.modalContainer, cn.modalContainer)}>
      <div className={mclasses.modalHeader}>
        <div className={mclasses.modalTitleContainer}>
          <span className={mclasses.modalHeaderTitle}>
            Изменить зарплату педагога
          </span>
        </div>
        <div className={mclasses.closeIcon} onClick={() => onClose()}>
          <IcoRemove />
        </div>
      </div>
      <div className={classNames(mclasses.formWrapper, cn.form)}>
        <div className={cn.header}>
          {teacher && <UserCard user={teacher} className={cn.headerBlock} />}
          <div className={classNames(cn.headerBlock, cn.groupInfo)}>
            <FieldWrapper label={groupNameSingular}>
              <span className={cn.groupName}>
                {isAllGroupsMode ? 'Для всех' : group?.name ?? '-'}
              </span>
            </FieldWrapper>
          </div>
        </div>
        <SelectField
          label="Тип зарплаты"
          description={salaryTypeTooltip}
          placeholder="Выберите тип зарплаты"
          value={salaryType}
          options={salaryTypeOptions}
          onChange={(value) =>
            setSalaryType(
              (value as TeacherSalaryType | null) ?? DEFAULT_SALARY_TYPE,
            )
          }
        />
        <TextField
          label={salaryLabel}
          value={salaryValue}
          onChange={onSalaryChange}
          placeholder="Введите значение"
          type="number"
        />
        {showMinAmountFields && (
          <div className={cn.minAmountBlock}>
            <div className={cn.minAmountCheckboxRow}>
              <CheckboxLabel
                label="Выплачивать минимальную сумму, если не набирает человеко-часы"
                value={minAmountEnabled}
                onChange={setMinAmountEnabled}
                className={cn.minAmountCheckbox}
              />
              <TooltipWrapper title={MIN_AMOUNT_TOOLTIP}>
                <span className={cn.infoIcon}>
                  <InfoIcon />
                </span>
              </TooltipWrapper>
            </div>
            {minAmountEnabled && (
              <TextField
                label="Впишите минимальную сумму"
                value={minAmount}
                onChange={onMinAmountChange}
                placeholder="Введите сумму"
                type="number"
              />
            )}
          </div>
        )}
        {showDurationField && (
          <TextField
            label="Впишите длительность занятия в минутах"
            value={durationMinutes}
            onChange={onDurationChange}
            placeholder="Введите длительность"
            type="number"
          />
        )}
      </div>
      <div className={mclasses.actions}>
        <SecondaryButton onClick={onClose}>
          Отменить
        </SecondaryButton>
        <PrimaryButton
          onClick={onConfirmSubmit}
          isLoading={submitting}
          disabled={submitting}
        >
          Сохранить
        </PrimaryButton>
      </div>
    </div>
  )
}

const EditTeacherSalaryModal: FC<EditTeacherSalaryModalProps> = (props) => {
  const { open, onClose } = props

  return (
    <Modal
      active={open}
      className={classNames(mclasses.modalWrapper, cn.modalWrapper) as any}
      setActive={() => onClose()}
    >
      <EditTeacherSalaryModalContent {...props} />
    </Modal>
  )
}

export default EditTeacherSalaryModal
